import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaCog,
  FaEdit,
  FaHeart,
  FaHome,
  FaKey,
  FaList,
  FaQuestionCircle,
  FaShoppingCart,
  FaSignOutAlt,
  FaUser,
} from "react-icons/fa";
import GlobalHeaderSearch from "./GlobalHeaderSearch";
import StorageService from "../services/StorageService";
import PlatformNotificationBell from "./PlatformNotificationBell";
import {
  getCurrentRole,
  getDashboardRouteByRole,
  getProfileRouteByRole,
  getViewProfileRouteByRole,
} from "../utils/roleSwitcher";
import { getDashboardRoute } from "../utils/getDashboardRoute";
import { getUserDisplayName } from "../utils/b2bHubHomeMedia";
import { buildLoginPath } from "../utils/authRedirect";
import { usePlatformLogoMeta } from "../hooks/usePlatformLogo";
import useMarketplaceCartCount from "../hooks/useMarketplaceCartCount";

function getUserInitials(user) {
  const name = getUserDisplayName(user);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

function getAccountRoleLabel(user) {
  if (!user) return "Visitor";
  const role = getCurrentRole();
  if (role?.name) return `${role.name} Account`;
  const typeId = String(user.user_type_id ?? "").trim();
  if (typeId.includes("1")) return "Buyer Account";
  if (typeId.includes("2") || typeId.includes("3")) return "Seller Account";
  if (typeId.includes("4")) return "Quality Inspector Account";
  return "Business Account";
}

export default function B2BHubHomeHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logoUrl: platformLogoUrl, isCustomLogo } = usePlatformLogoMeta({ variant: "onDark" });
  const cartCount = useMarketplaceCartCount();
  const [user, setUser] = useState(() => StorageService.getUser());
  const [isAuthed, setIsAuthed] = useState(() => StorageService.isAuthenticated());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const menuPanelRef = useRef(null);

  const syncAuth = useCallback(() => {
    setUser(StorageService.getUser());
    setIsAuthed(StorageService.isAuthenticated());
  }, []);

  useEffect(() => {
    syncAuth();
    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, [syncAuth]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (
        menuBtnRef.current?.contains(e.target) ||
        menuPanelRef.current?.contains(e.target)
      ) {
        return;
      }
      setMenuOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const displayName = getUserDisplayName(user);
  const roleLabel = getAccountRoleLabel(user);
  const initials = getUserInitials(user);
  const dashboardTo = isAuthed ? getDashboardRoute() || getDashboardRouteByRole() : "/login";
  const loginReturnTo = `${location.pathname}${location.search}`;

  const handleLogout = () => {
    StorageService.clearAuth();
    setUser(null);
    setIsAuthed(false);
    setMenuOpen(false);
    navigate("/login");
  };

  return (
    <header className="hub-home-header">
      <div className="hub-home-header__topbar">
        <div className="container-fluid hub-home-header__topbar-inner">
          <Link to="/home" className="hub-home-header__brand" aria-label="Trocanet home">
            <img
              src={platformLogoUrl}
              alt="Trocanet"
              className={`hub-home-header__brand-logo${
                isCustomLogo ? " hub-home-header__brand-logo--on-light-surface" : ""
              }`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.parentElement?.querySelector(".hub-home-header__brand-fallback");
                if (fallback) fallback.style.display = "inline-flex";
              }}
            />
            <span className="hub-home-header__brand-fallback" aria-hidden>
              Trocanet
            </span>
          </Link>

          <GlobalHeaderSearch />

          <div className="hub-home-header__actions">
            {isAuthed ? (
              <Link
                to="/my-cart"
                className="hub-home-header__icon-btn hub-home-header__icon-btn--dark hub-home-header__cart-wrap"
                title="View cart"
                aria-label={`View cart${cartCount ? `, ${cartCount} items` : ""}`}
              >
                <FaShoppingCart aria-hidden />
                {cartCount > 0 ? (
                  <span className="hub-home-header__cart-badge" aria-hidden>
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                ) : null}
              </Link>
            ) : (
              <Link
                to={buildLoginPath("/my-cart")}
                state={{ from: "/my-cart" }}
                className="hub-home-header__icon-btn hub-home-header__icon-btn--dark"
                title="Sign in to view cart"
                aria-label="Sign in to view cart"
              >
                <FaShoppingCart aria-hidden />
              </Link>
            )}

            {isAuthed ? (
              <>
                <Link to={dashboardTo} className="hub-home-header__nav-link" title="Go to Dashboard">
                  <FaHome aria-hidden />
                  <span>Dashboard</span>
                </Link>

                <PlatformNotificationBell
                  variant="light"
                  buttonClassName="hub-home-header__icon-btn hub-home-header__icon-btn--dark"
                />

                <div className="hub-home-header__profile-wrap">
                  <button
                    ref={menuBtnRef}
                    type="button"
                    className="hub-home-header__profile hub-home-header__profile--dark"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((v) => !v)}
                  >
                    <span className="hub-home-header__avatar" aria-hidden>
                      {initials}
                    </span>
                    <span className="hub-home-header__profile-text">
                      <span className="hub-home-header__profile-name">{displayName}</span>
                      <span className="hub-home-header__profile-role">{roleLabel}</span>
                    </span>
                    <FaChevronDown
                      className={`hub-home-header__chevron${menuOpen ? " hub-home-header__chevron--open" : ""}`}
                      aria-hidden
                    />
                  </button>

                  {menuOpen ? (
                    <div ref={menuPanelRef} className="hub-home-header__menu" role="menu">
                      <ul className="hub-home-header__menu-list">
                        <li>
                          <Link to={getViewProfileRouteByRole()} role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaUser aria-hidden /> My Profile
                          </Link>
                        </li>
                        <li>
                          <Link to={getProfileRouteByRole()} role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaEdit aria-hidden /> Edit Profile
                          </Link>
                        </li>
                        <li>
                          <Link to="/settings" role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaCog aria-hidden /> Settings
                          </Link>
                        </li>
                        <li>
                          <Link to="/help-center" role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaQuestionCircle aria-hidden /> Help
                          </Link>
                        </li>
                        <li>
                          <Link to="/my-wishlist" role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaHeart aria-hidden /> My Wishlist
                          </Link>
                        </li>
                        <li>
                          <Link to="/my-saved-lists" role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaList aria-hidden /> My Saved Lists
                          </Link>
                        </li>
                        <li>
                          <Link to="/change-password" role="menuitem" onClick={() => setMenuOpen(false)}>
                            <FaKey aria-hidden /> Change Password
                          </Link>
                        </li>
                        <li className="hub-home-header__menu-divider" role="separator" />
                        <li>
                          <button type="button" className="hub-home-header__menu-logout" onClick={handleLogout} role="menuitem">
                            <FaSignOutAlt aria-hidden /> Logout
                          </button>
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <Link
                  to={buildLoginPath(loginReturnTo)}
                  state={{ from: loginReturnTo }}
                  className="hub-home-header__auth-btn hub-home-header__auth-btn--login"
                >
                  Login
                </Link>
                <Link to="/signup" className="hub-home-header__auth-btn hub-home-header__auth-btn--signup">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
